import {
    Avatar,
    Box,
    Button,
    HStack,
    Image,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    Switch,
    Text,
    Textarea,
    useToast,
    VStack,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import MyIcon from "../../../../../components/Icon/MyIcon";
import { colors } from "../../../../../styles";
import ViewTransition, { SlideDirection } from "./ViewTransition";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IMyProfile, IProfile } from "../../../../../types";
import { getMyProfile, IUpdateProfilePayload, updateProfile, uploadFileForAvatar } from "../../../../../api";
import { getApiErrorMessage, typeToKor } from "../../../../../utils";

/* 모달 내부 화면 전환 단계 */

export type EditView = "MAIN" | "NICKNAME" | "BIO";

const MAX_NICKNAME_LENGTH = 20;
const MAX_BIO_LENGTH = 100;

const listItemBorder = {
    borderBottom: "0.2px solid",
    borderColor: colors.g4,
};

interface EditModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function EditModal(props: EditModalProps) {

    /* state */

    const fileInputRef = useRef<HTMLInputElement>(null);
    const avatarPreviewUrlRef = useRef<string | null>(null);

    const toast = useToast();
    const queryClient = useQueryClient();

    /* 현재 표시 중인 화면 — MAIN에서 NICKNAME/INTRO로 전환 */

    const [currentView, setCurrentView] = useState<EditView>("MAIN");

    /* 슬라이드 방향 — forward: 메인→서브, back: 서브→메인 */

    const [slideDirection, setSlideDirection] = useState<SlideDirection>("forward");

    /* 임시 저장 값 */

    const [draftAvatarUrl, setDraftAvatarUrl] = useState<string | null>(null);
    const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
    const [isAvatarRemoved, setIsAvatarRemoved] = useState(false);

    const [draftNickname, setDraftNickname] = useState("");
    const [draftBio, setDraftBio] = useState("");
    const [draftVisibility, setDraftVisibility] = useState<"PUBLIC" | "PRIVATE">("PUBLIC");

    /* 서브 화면 편집 값 */

    const [editingNickname, setEditingNickname] = useState("");
    const [editingBio, setEditingBio] = useState("");

    /* query: getMyProfile */

    const { data: myProfile } = useQuery<IMyProfile>({
        queryKey: ["getMyProfile"],
        queryFn: () => getMyProfile(),
        enabled: props.isOpen,
    });

    const myType = typeToKor(myProfile?.user?.type ?? null);

    /* -------------------------------------------------------------- */

    /* ----- 공통 ----- */

    /* -------------------------------------------------------------- */

    /* revokeAvatarPreview: 프로필 이미지 미리보기 취소 */

    const revokeAvatarPreview = () => {
        if (avatarPreviewUrlRef.current) {
            URL.revokeObjectURL(avatarPreviewUrlRef.current);
            avatarPreviewUrlRef.current = null;
        }
    };

    /* resetDraftFromProfile: 프로필 정보를 기반으로 임시 저장 값 초기화 */

    const resetDraftFromProfile = (profile: IMyProfile) => {

        revokeAvatarPreview();

        /////////////////////////////////////////////////////////////////

        setDraftAvatarUrl(profile.avatar ?? null);
        setPendingAvatarFile(null);
        setIsAvatarRemoved(false);

        /////////////////////////////////////////////////////////////////

        setDraftNickname(profile.nickname);
        setDraftBio(profile.bio ?? "");
        setDraftVisibility(profile.visibility);

        /////////////////////////////////////////////////////////////////

        setEditingNickname(profile.nickname);
        setEditingBio(profile.bio ?? "");
    };

    /* useEffect: 모달 열릴 때 프로필로 초기화 */

    useEffect(() => {
        if (props.isOpen && myProfile) {
            resetDraftFromProfile(myProfile);
        }
    }, [props.isOpen, myProfile]);

    /* useEffect: 모달 종료 시 초기화 */

    useEffect(() => {
        if (!props.isOpen) {
            setCurrentView("MAIN");
            revokeAvatarPreview();
        }
    }, [props.isOpen]);

    /* updateProfileMutation */

    const updateProfileMutation = useMutation({
        mutationFn: async () => {

            if (!myProfile) {
                throw new Error("프로필 정보를 불러오지 못했습니다.");
            }

            const nickname = draftNickname.trim();
            const bio = draftBio;
            const savedBio = myProfile.bio ?? "";

            const payload: IUpdateProfilePayload = {};

            if (pendingAvatarFile) {
                payload.avatar = await uploadFileForAvatar({ profileId: myProfile.id, file: pendingAvatarFile });
            } else if (isAvatarRemoved && myProfile.avatar) {
                payload.avatar = null;
            }

            if (nickname !== myProfile.nickname) {
                payload.nickname = nickname;
            }
            if (bio !== savedBio) {
                payload.bio = bio;
            }
            if (draftVisibility !== myProfile.visibility) {
                payload.visibility = draftVisibility;
            }

            if (Object.keys(payload).length === 0) {
                return null;
            }

            return updateProfile(payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getMyProfile"] });
            props.onClose();
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: getApiErrorMessage(error, "프로필 저장에 실패했습니다."),
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* ── 화면 전환 ── */

    const backToMain = () => {
        setSlideDirection("back");
        setCurrentView("MAIN");
    };

    /* -------------------------------------------------------------- */

    /* ----- MAIN ----- */

    /* -------------------------------------------------------------- */

    const handleUploadPhoto = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        revokeAvatarPreview();
        avatarPreviewUrlRef.current = URL.createObjectURL(file);
        setPendingAvatarFile(file);
        setDraftAvatarUrl(avatarPreviewUrlRef.current);
        setIsAvatarRemoved(false);
        event.target.value = "";
    };

    const handleDeletePhoto = () => {
        revokeAvatarPreview();
        setPendingAvatarFile(null);
        setDraftAvatarUrl(null);
        setIsAvatarRemoved(true);
    };

    const handleComplete = () => {
        const trimmedNickname = draftNickname.trim();

        if (!trimmedNickname) {
            toast({
                title: "닉네임을 입력해 주세요.",
                status: "warning",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        if (trimmedNickname.length > MAX_NICKNAME_LENGTH) {
            toast({
                title: `닉네임은 ${MAX_NICKNAME_LENGTH}자 이하로 입력해 주세요.`,
                status: "warning",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        if (draftBio.length > MAX_BIO_LENGTH) {
            toast({
                title: `소개는 ${MAX_BIO_LENGTH}자 이하로 입력해 주세요.`,
                status: "warning",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        updateProfileMutation.mutate();
    };

    /* -------------------------------------------------------------- */

    /* ----- NICKNAME ----- */

    /* -------------------------------------------------------------- */

    const openNicknameView = () => {
        setEditingNickname(draftNickname);
        setSlideDirection("forward");
        setCurrentView("NICKNAME");
    };

    const handleNicknameDone = () => {
        const trimmed = editingNickname.trim();

        if (!trimmed) {
            toast({
                title: "닉네임을 입력해 주세요.",
                status: "warning",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        if (trimmed.length > MAX_NICKNAME_LENGTH) {
            toast({
                title: `닉네임은 ${MAX_NICKNAME_LENGTH}자 이하로 입력해 주세요.`,
                status: "warning",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        setDraftNickname(trimmed);
        setSlideDirection("back");
        setCurrentView("MAIN");
    };

    /* -------------------------------------------------------------- */

    /* ----- BIO ----- */

    /* -------------------------------------------------------------- */

    const openBioView = () => {
        setEditingBio(draftBio);
        setSlideDirection("forward");
        setCurrentView("BIO");
    };

    const handleBioDone = () => {
        if (editingBio.length > MAX_BIO_LENGTH) {
            toast({
                title: `소개는 ${MAX_BIO_LENGTH}자 이하로 입력해 주세요.`,
                status: "warning",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        setDraftBio(editingBio);
        setSlideDirection("back");
        setCurrentView("MAIN");
    };

    /* render */

    return (
        <Modal
            motionPreset="slideInBottom"
            size="2xl"
            isOpen={props.isOpen}
            onClose={props.onClose}
        >
            <ModalOverlay />
            <ModalContent borderRadius={16} overflow="hidden">

                <ModalHeader
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    borderBottom="0.2px solid"
                    borderColor={colors.g4}
                    padding={3}
                    minHeight="52px"
                >
                    {currentView === "MAIN" && (
                        <Text
                            as="button"
                            type="button"
                            fontSize={16}
                            fontWeight={500}
                            color={colors.black}
                            cursor="pointer"
                            onClick={props.onClose}
                        >
                            취소
                        </Text>
                    )}

                    {currentView === "NICKNAME" && (
                        <>
                            <Box
                                as="button"
                                type="button"
                                display="flex"
                                alignItems="center"
                                onClick={backToMain}
                            >
                                <MyIcon
                                    src="/svg/arrow-left.svg"
                                    alt="뒤로가기"
                                    width={24}
                                    height={24}
                                />
                            </Box>

                            <Text fontSize={16} fontWeight={700} color={colors.g1}>
                                닉네임 수정
                            </Text>

                            <Text
                                as="button"
                                type="button"
                                fontSize={16}
                                fontWeight={600}
                                color={colors.blue}
                                cursor="pointer"
                                onClick={handleNicknameDone}
                            >
                                완료
                            </Text>
                        </>
                    )}

                    {currentView === "BIO" && (
                        <>
                            <Box
                                as="button"
                                type="button"
                                display="flex"
                                alignItems="center"
                                onClick={backToMain}
                            >
                                <MyIcon
                                    src="/svg/arrow-left.svg"
                                    alt="뒤로가기"
                                    width={24}
                                    height={24}
                                />
                            </Box>

                            <Text fontSize={16} fontWeight={700} color={colors.g1}>
                                소개 수정
                            </Text>

                            <Text
                                as="button"
                                type="button"
                                fontSize={16}
                                fontWeight={600}
                                color={colors.blue}
                                cursor="pointer"
                                onClick={handleBioDone}
                            >
                                완료
                            </Text>
                        </>
                    )}
                </ModalHeader>

                <ModalBody padding={0} overflow="hidden">
                    <ViewTransition viewKey={currentView} direction={slideDirection}>

                        {currentView === "MAIN" && (
                            <Stack spacing={0}>
                                <VStack paddingY={6} spacing={3}>
                                    <Menu placement="bottom">
                                        <MenuButton
                                            as={Box}
                                            cursor="pointer"
                                            borderRadius="full"
                                            _focus={{ boxShadow: "none" }}
                                        >
                                            {draftAvatarUrl ? (
                                                <Image
                                                    src={draftAvatarUrl}
                                                    alt="프로필 이미지"
                                                    boxSize={20}
                                                    borderRadius="full"
                                                    objectFit="cover"
                                                />
                                            ) : (
                                                <Image 
                                                    src={"/png/profile.png"}
                                                    alt="프로필 이미지"
                                                    boxSize={20}
                                                    borderRadius="full"
                                                    objectFit="cover"
                                                />
                                            )}
                                        </MenuButton>
                                        <MenuList minWidth="160px" paddingY={1}>
                                            <MenuItem
                                                fontSize={15}
                                                fontWeight={500}
                                                onClick={handleUploadPhoto}
                                            >
                                                사진 업로드
                                            </MenuItem>
                                            {draftAvatarUrl && (
                                                <MenuItem
                                                    fontSize={15}
                                                    fontWeight={500}
                                                    color={colors.red}
                                                    onClick={handleDeletePhoto}
                                                >
                                                    현재 사진 삭제
                                                </MenuItem>
                                            )}
                                        </MenuList>
                                    </Menu>
                                </VStack>

                                <Stack spacing={1} paddingX={5} paddingY={4} {...listItemBorder}>
                                    <Text fontSize={14} fontWeight={500} color={colors.g3}>
                                        가입 유형
                                    </Text>
                                    <Text fontSize={16} fontWeight={500} color={colors.g1}>
                                        {myType}
                                    </Text>
                                </Stack>

                                <HStack
                                    as="button"
                                    type="button"
                                    width="100%"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    paddingX={5}
                                    paddingY={4}
                                    textAlign="left"
                                    _hover={{ backgroundColor: colors.g5 }}
                                    onClick={openNicknameView}
                                    {...listItemBorder}
                                >
                                    <Stack spacing={1} flex={1} minWidth={0}>
                                        <Text fontSize={14} fontWeight={500} color={colors.g3}>
                                            닉네임
                                        </Text>
                                        <Text
                                            fontSize={16}
                                            fontWeight={500}
                                            color={colors.g1}
                                            noOfLines={1}
                                        >
                                            {draftNickname}
                                        </Text>
                                    </Stack>
                                    <Box flexShrink={0}>
                                        <MyIcon
                                            src="/svg/arrow-right.svg"
                                            alt="수정"
                                            width={20}
                                            height={20}
                                        />
                                    </Box>
                                </HStack>

                                <HStack
                                    as="button"
                                    type="button"
                                    width="100%"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    paddingX={5}
                                    paddingY={4}
                                    textAlign="left"
                                    _hover={{ backgroundColor: colors.g5 }}
                                    onClick={openBioView}
                                    {...listItemBorder}
                                >
                                    <Stack spacing={1} flex={1} minWidth={0}>
                                        <Text fontSize={14} fontWeight={500} color={colors.g3}>
                                            소개
                                        </Text>
                                        <Text
                                            fontSize={16}
                                            fontWeight={500}
                                            color={draftBio ? colors.g1 : colors.g3}
                                            noOfLines={1}
                                        >
                                            {draftBio || "소개를 입력해 주세요"}
                                        </Text>
                                    </Stack>
                                    <Box flexShrink={0}>
                                        <MyIcon
                                            src="/svg/arrow-right.svg"
                                            alt="수정"
                                            width={20}
                                            height={20}
                                        />
                                    </Box>
                                </HStack>

                                <Stack spacing={2} paddingX={5} paddingY={4} {...listItemBorder}>
                                    <HStack justifyContent="space-between" alignItems="center">
                                        <Text fontSize={16} fontWeight={500} color={colors.g1}>
                                            프로필 공개 여부
                                        </Text>
                                        <Switch
                                            isChecked={draftVisibility === "PUBLIC"}
                                            onChange={(event) =>
                                                setDraftVisibility(event.target.checked ? "PUBLIC" : "PRIVATE")
                                            }
                                            colorScheme="blue"
                                        />
                                    </HStack>
                                    <Text fontSize={12} fontWeight={400} color={colors.g3}>
                                        비공개로 전환하면 나만 볼 수 있습니다.
                                    </Text>
                                </Stack>
                            </Stack>
                        )}

                        {currentView === "NICKNAME" && (
                            <Stack padding={5} spacing={4}>
                                <Input
                                    value={editingNickname}
                                    onChange={(event) => setEditingNickname(event.target.value)}
                                    placeholder="닉네임을 입력해 주세요"
                                    variant="unstyled"
                                    fontSize={16}
                                    fontWeight={400}
                                    color={colors.g1}
                                    maxLength={MAX_NICKNAME_LENGTH}
                                    _placeholder={{ color: colors.g3 }}
                                />
                            </Stack>
                        )}

                        {currentView === "BIO" && (
                            <Stack padding={5} spacing={4} minHeight="240px">
                                <Textarea
                                    value={editingBio}
                                    onChange={(event) => setEditingBio(event.target.value)}
                                    placeholder="소개를 입력해 주세요"
                                    variant="unstyled"
                                    fontSize={16}
                                    fontWeight={400}
                                    color={colors.g1}
                                    minHeight={160}
                                    resize="none"
                                    padding={0}
                                    maxLength={MAX_BIO_LENGTH}
                                    _placeholder={{ color: colors.g3 }}
                                />
                                <Text
                                    marginTop="auto"
                                    textAlign="center"
                                    fontSize={12}
                                    fontWeight={400}
                                    color={colors.g3}
                                >
                                    {editingBio.length}/{MAX_BIO_LENGTH}
                                </Text>
                            </Stack>
                        )}

                    </ViewTransition>
                </ModalBody>

                {currentView === "MAIN" && (
                    <ModalFooter padding={4}>
                        <Button
                            width="100%"
                            height="48px"
                            borderRadius="full"
                            backgroundColor={colors.black}
                            color={colors.white}
                            fontSize={16}
                            fontWeight={700}
                            _hover={{ backgroundColor: colors.dark }}
                            _active={{ backgroundColor: colors.dark }}
                            isLoading={updateProfileMutation.isPending}
                            onClick={handleComplete}
                        >
                            완료
                        </Button>
                    </ModalFooter>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                />
            </ModalContent>
        </Modal>
    );
}
